import { useCallback, useEffect, useState } from "react";
import { useOnMount } from "../../../../../../hooks/useOnMount.ts";
import {
  rgbaStringToRgba,
  rgbaToString,
} from "../../../../../../utils/colors.ts";
import { TEXT_LAYER_TYPE } from "../../Designer/constants.ts";
import { useLayerRotationEventListener } from "../../Designer/hooks/useLayerRotationEventListener.ts";
import { useLayerSelectionEventListener } from "../../Designer/hooks/useLayerSelectionEventListener.ts";
import { useSelectedLayerType } from "../../Designer/hooks/useSelectedLayerType.ts";
import { designerCanvasStore } from "../../Designer/stores/designerCanvasStore.ts";
import type { TextLayer, TextLayerSettings } from "../../Designer/types.ts";
import {
  getSpecificActiveLayers,
  normalizeLayerAngle,
} from "../../Designer/utils/layer.ts";
import {
  updateTextLayer,
  updateTextSelection,
} from "../../Designer/utils/text.ts";
import { DesignerColorPickerButton } from "../components/DesignerSettings/DesignerColorPickerButton.tsx";
import { DesignerRotationInputNumber } from "../components/DesignerSettings/DesignerRotationInputNumber.tsx";
import { ShadowSettingsSection } from "../components/DesignerSettings/ShadowSettingsSection.tsx";
import { StrokeSettingsSection } from "../components/DesignerSettings/StrokeSettingsSection.tsx";
import { DesignerSettingsItem } from "../components/Layout/DesignerSettingsItem.tsx";
import { DesignerSettingsLayout } from "../components/Layout/DesignerSettingsLayout.tsx";
import { FontFamilySelectionDropdown } from "./components/FontFamilySelectionDropdown.tsx";
import { FontSizeInputNumber } from "./components/FontSizeInputNumber.tsx";
import { LetterSpacingSlider } from "./components/LetterSpacingSlider.tsx";
import { LineHeightSlider } from "./components/LineHeightSlider.tsx";
import { StyleButtons } from "./components/StyleButtons.tsx";
import { TextAligmentSelection } from "./components/TextAligmentSelection.tsx";
import { TextEmptyState } from "./TextEmptyState.tsx";

export const EditTextSettings = () => {
  const { fabricCanvas, isTextEditing } = designerCanvasStore.useState(
    (it) => ({
      fabricCanvas: it.fabricCanvas,
      isTextEditing: it.isTextEditing,
    }),
  );
  const selectedLayerType = useSelectedLayerType();
  const { selectedTextLayersSettings, setSelectedTextLayersSettings } =
    useSelectedTextLayersSettings();

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const activeTextLayers = getSpecificActiveLayers({
        fabricCanvas,
        layerType: TEXT_LAYER_TYPE,
      });

      if (activeTextLayers.length === 0) return;

      const isCtrlOrCmd = e.ctrlKey || e.metaKey;

      if (isCtrlOrCmd && !e.altKey) {
        switch (e.key.toLowerCase()) {
          case "b":
            e.preventDefault();
            setSelectedTextLayersSettings({
              isBold: !selectedTextLayersSettings.isBold,
            });
            break;
          case "i":
            e.preventDefault();
            setSelectedTextLayersSettings({
              isItalic: !selectedTextLayersSettings.isItalic,
            });
            break;
          case "u":
            e.preventDefault();
            setSelectedTextLayersSettings({
              isUnderline: !selectedTextLayersSettings.isUnderline,
            });
            break;
        }
      }

      if (isCtrlOrCmd && e.shiftKey && e.key.toLowerCase() === "x") {
        e.preventDefault();
        setSelectedTextLayersSettings({
          isStrikethrough: !selectedTextLayersSettings.isStrikethrough,
        });
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [fabricCanvas, selectedTextLayersSettings, setSelectedTextLayersSettings]);

  return selectedLayerType === TEXT_LAYER_TYPE ? (
    <DesignerSettingsLayout>
      <DesignerSettingsItem
        title="Font Family"
        rightItem={
          <FontFamilySelectionDropdown
            fontFamily={selectedTextLayersSettings.fontFamily}
            onFontFamilyChange={(fontFamily) => {
              setSelectedTextLayersSettings({
                fontFamily,
              });
            }}
          />
        }
      />
      <DesignerSettingsItem
        title="Color"
        rightItem={
          <DesignerColorPickerButton
            color={rgbaStringToRgba(selectedTextLayersSettings.textColor)}
            onColorChange={(textColor) =>
              setSelectedTextLayersSettings({
                textColor: rgbaToString(textColor),
              })
            }
          />
        }
      />
      <DesignerSettingsItem
        title="Size"
        rightItem={
          <FontSizeInputNumber
            fontSize={selectedTextLayersSettings.fontSize}
            onFontSizeChange={(fontSize) =>
              setSelectedTextLayersSettings({ fontSize })
            }
          />
        }
      />
      <DesignerSettingsItem
        title="Style"
        rightItem={
          <StyleButtons
            selectedTextLayerStyle={selectedTextLayersSettings}
            setSelectedTextLayerStyle={setSelectedTextLayersSettings}
          />
        }
      />
      {!isTextEditing && (
        <>
          <DesignerSettingsItem
            title="Letter Spacing"
            rightItem={
              <LetterSpacingSlider
                charSpacing={selectedTextLayersSettings.charSpacing}
                fontSize={selectedTextLayersSettings.fontSize}
                onCharSpacingChange={(charSpacing) =>
                  setSelectedTextLayersSettings({ charSpacing })
                }
              />
            }
          />
          <DesignerSettingsItem
            title="Line Spacing"
            rightItem={
              <LineHeightSlider
                lineHeight={selectedTextLayersSettings.lineHeight}
                fontSize={selectedTextLayersSettings.fontSize}
                onLineHeightChange={(lineHeight) =>
                  setSelectedTextLayersSettings({ lineHeight })
                }
              />
            }
          />
          <DesignerSettingsItem
            title="Rotation"
            rightItem={
              <DesignerRotationInputNumber
                rotation={selectedTextLayersSettings.rotation}
                onRotationChange={(rotation) =>
                  setSelectedTextLayersSettings({ rotation })
                }
              />
            }
          />
          <DesignerSettingsItem
            title="Alignment"
            rightItem={
              // FIXME: Implement base component `SegmentedControl` and use it here (https://www.figma.com/design/SkWiXuJ70Vnl7mB8EyGlqv/Chili---Design-System?node-id=219-49&p=f&t=MdQKV8aYTLdbW0FN-0)
              <TextAligmentSelection
                alignment={selectedTextLayersSettings.textAlign}
                setAlignment={(textAlign) =>
                  setSelectedTextLayersSettings({ textAlign })
                }
              />
            }
          />
          <DesignerSettingsItem title="Shadow">
            <ShadowSettingsSection
              shadowSettings={selectedTextLayersSettings}
              onShadowSettingsChange={setSelectedTextLayersSettings}
            />
          </DesignerSettingsItem>
          <DesignerSettingsItem title="Stroke">
            <StrokeSettingsSection
              strokeSettings={{
                strokeColor: selectedTextLayersSettings.stroke,
                strokeStyle: selectedTextLayersSettings.strokeStyle,
                strokeWidth: selectedTextLayersSettings.strokeWidth,
              }}
              onStrokeSettingsChange={(strokeSettings) => {
                const { strokeColor, strokeStyle, strokeWidth } =
                  strokeSettings;
                setSelectedTextLayersSettings({
                  stroke: strokeColor ?? selectedTextLayersSettings.stroke,
                  strokeStyle:
                    strokeStyle ?? selectedTextLayersSettings.strokeStyle,
                  strokeWidth:
                    strokeWidth ?? selectedTextLayersSettings.strokeWidth,
                });
              }}
              minThickness={0}
              maxThickness={10}
            />
          </DesignerSettingsItem>
        </>
      )}
    </DesignerSettingsLayout>
  ) : (
    <TextEmptyState />
  );
};

const useSelectedTextLayersSettings = () => {
  const { fabricCanvas } = designerCanvasStore.useState();

  const [selectedTextLayersSettings, setSelectedTextLayersSettings] = useState<
    Required<TextLayerSettings>
  >({
    fontFamily: "Arial",
    fontSize: 20,
    textColor: "rgba(0, 0, 0, 1)",
    textAlign: "left",
    isBold: false,
    isItalic: false,
    isUnderline: false,
    isStrikethrough: false,
    rotation: 0,
    charSpacing: 0,
    lineHeight: 1.2,
    shadowStyle: "none",
    shadowDirection: "top",
    shadowDistance: "small",
    stroke: "rgba(0, 0, 0, 1)",
    strokeStyle: "solid",
    strokeWidth: 0,
  });

  const updateTextLayerSettingsFromTextLayer = useCallback(
    (textLayer: TextLayer) => {
      setSelectedTextLayersSettings({
        fontFamily: textLayer.fontFamily,
        fontSize: textLayer.fontSize,
        textColor: textLayer.fill as string,
        textAlign: textLayer.textAlign,
        isBold: textLayer.fontWeight === "bold",
        isItalic: textLayer.fontStyle === "italic",
        isUnderline: textLayer.underline,
        isStrikethrough: textLayer.linethrough,
        rotation: normalizeLayerAngle(textLayer.angle),
        charSpacing: textLayer.charSpacing,
        lineHeight: textLayer.lineHeight,
        shadowStyle: textLayer.shadowStyle,
        shadowDirection: textLayer.shadowDirection,
        shadowDistance: textLayer.shadowDistance,
        stroke: textLayer.stroke,
        strokeStyle: textLayer.strokeStyle,
        strokeWidth: textLayer.strokeWidth,
      });
    },
    [],
  );

  useOnMount(() => {
    if (!fabricCanvas) return;
    const activeTextLayers = getSpecificActiveLayers({
      fabricCanvas,
      layerType: TEXT_LAYER_TYPE,
    });
    if (activeTextLayers.length === 0) return;
    updateTextLayerSettingsFromTextLayer(activeTextLayers[0]);
  });

  useLayerSelectionEventListener({
    fabricCanvas,
    layerType: TEXT_LAYER_TYPE,
    onLayerSelectionUpdate: updateTextLayerSettingsFromTextLayer,
  });

  useLayerRotationEventListener({
    fabricCanvas,
    layerType: TEXT_LAYER_TYPE,
    onLayerRotationEvent: (rotation) => {
      setSelectedTextLayersSettings({
        ...selectedTextLayersSettings,
        rotation,
      });
    },
  });

  const updateSelectedTextLayersSettings = useCallback(
    (options: Partial<Required<TextLayerSettings>>) => {
      setSelectedTextLayersSettings({
        ...selectedTextLayersSettings,
        ...options,
      });
      if (!fabricCanvas) return;
      for (const layer of getSpecificActiveLayers({
        fabricCanvas,
        layerType: TEXT_LAYER_TYPE,
      })) {
        if (layer.isEditing) {
          updateTextSelection(layer, options);
        } else {
          updateTextLayer(layer, options);
        }
      }
      fabricCanvas.renderAll();
    },
    [fabricCanvas, selectedTextLayersSettings],
  );

  return {
    selectedTextLayersSettings,
    setSelectedTextLayersSettings: updateSelectedTextLayersSettings,
  };
};
